import { assembleGrid } from "@/grid/data/assemble-grid";
import {
  dailyFetchFrom,
  priorWeekMonday,
  quarterStart,
  toIsoDate,
  nowToronto,
} from "@/grid/core/periods";
import type {
  DepartmentDefinition,
  GridRawData,
  GridViewModel,
  TargetRow,
} from "@/grid/core/types";
import { fetchTargetMetricConfigs } from "@/targets/api/target-config-api";
import { supabase } from "@/lib/supabase";

const PAGE_SIZE = 1000;

async function fetchAll<T>(
  fetchPage: (from: number, to: number) => Promise<{ data: T[] | null; error: Error | null }>,
): Promise<T[]> {
  const rows: T[] = [];
  let from = 0;

  while (true) {
    const to = from + PAGE_SIZE - 1;
    const { data, error } = await fetchPage(from, to);
    if (error) throw error;
    if (!data || data.length === 0) break;
    rows.push(...data);
    if (data.length < PAGE_SIZE) break;
    from += PAGE_SIZE;
  }

  return rows;
}

function periodFetchFrom(asOf = nowToronto()): string {
  const candidates = [priorWeekMonday(asOf), quarterStart(asOf)];
  return candidates.sort()[0]!;
}

function resolvePeriodFrom(
  asOf = nowToronto(),
  options?: { dailyFrom?: string; periodFrom?: string },
): string {
  const candidates = [periodFetchFrom(asOf)];
  if (options?.periodFrom) candidates.push(options.periodFrom);
  if (options?.dailyFrom) candidates.push(options.dailyFrom);
  return candidates.sort()[0]!;
}

export async function fetchGridRawData(
  department: DepartmentDefinition,
  asOf = nowToronto(),
  options?: { dailyFrom?: string; periodFrom?: string },
): Promise<GridRawData> {
  const dailyFrom = options?.dailyFrom ?? dailyFetchFrom(asOf);
  const dailyTo = toIsoDate(asOf);
  const periodFrom = resolvePeriodFrom(asOf, options);
  const needsRepPeriodMetrics = department.metrics.some(
    (metric) => metric.source.kind === "rep-period",
  );

  const [
    teams,
    reps,
    dailyActivities,
    repPeriodMetrics,
    teamPeriodMetrics,
    targets,
    targetMetricConfigs,
  ] = await Promise.all([
    fetchAll(async (from, to) => {
      const { data, error } = await supabase
        .from("teams")
        .select("id, name, region, department, syncore_ref")
        .eq("active", true)
        .eq("department", department.id)
        .order("name")
        .range(from, to);
      return {
        data:
          data
            ?.filter(
              (
                row,
              ): row is typeof row & {
                id: number;
                name: string;
                region: string;
                department: string;
                syncore_ref: string | null;
              } =>
                row.id != null && row.name != null && row.region != null && row.department != null,
            )
            .map((row) => ({
              id: row.id,
              name: row.name,
              region: row.region,
              department: row.department,
              syncore_ref: row.syncore_ref,
            })) ?? null,
        error: error as Error | null,
      };
    }),
    fetchAll(async (from, to) => {
      const { data, error } = await supabase
        .from("reps")
        .select("id, team_id, teams!inner(department)")
        .eq("active", true)
        .eq("teams.department", department.id)
        .range(from, to);
      return {
        data:
          data
            ?.filter(
              (row): row is typeof row & { id: number; team_id: number } =>
                row.id != null && row.team_id != null,
            )
            .map((row) => ({ id: row.id, team_id: row.team_id })) ?? null,
        error: error as Error | null,
      };
    }),
    fetchAll(async (from, to) => {
      const { data, error } = await supabase
        .from("daily_activities")
        .select(
          `
          rep_id,
          activity_date,
          total_calls,
          connected_calls,
          meetings_held,
          sequences,
          reps!inner (
            teams!inner (department)
          )
        `,
        )
        .eq("reps.teams.department", department.id)
        .gte("activity_date", dailyFrom)
        .lte("activity_date", dailyTo)
        .range(from, to);
      return {
        data:
          data?.map((row) => ({
            rep_id: row.rep_id as number,
            activity_date: row.activity_date as string,
            total_calls: row.total_calls,
            connected_calls: row.connected_calls,
            meetings_held: row.meetings_held,
            sequences: row.sequences,
          })) ?? null,
        error: error as Error | null,
      };
    }),
    fetchAll(async (from, to) => {
      if (!needsRepPeriodMetrics) {
        return { data: [], error: null };
      }

      const { data, error } = await supabase
        .from("period_metrics")
        .select(
          `
          rep_id,
          period_type,
          period_start,
          sqls,
          reps!inner (
            teams!inner (department)
          )
        `,
        )
        .eq("reps.teams.department", department.id)
        .eq("period_type", "week")
        .not("sqls", "is", null)
        .gte("period_start", periodFrom)
        .range(from, to);
      return {
        data:
          data?.map((row) => ({
            rep_id: row.rep_id as number,
            period_type: row.period_type as string,
            period_start: row.period_start as string,
            sqls: row.sqls as number | null,
          })) ?? null,
        error: error as Error | null,
      };
    }),
    fetchAll(async (from, to) => {
      const { data, error } = await supabase
        .from("team_period_metrics")
        .select(
          `
          team_id,
          period_type,
          period_start,
          sales_amount,
          opps_generated_count,
          opps_generated_value,
          teams!inner (department)
        `,
        )
        .eq("teams.department", department.id)
        .gte("period_start", periodFrom)
        .in("period_type", ["week", "month"])
        .range(from, to);
      return {
        data:
          data?.map((row) => ({
            team_id: row.team_id as number,
            period_type: row.period_type as string,
            period_start: row.period_start as string,
            sales_amount: row.sales_amount,
            opps_generated_count: row.opps_generated_count,
            opps_generated_value: row.opps_generated_value,
          })) ?? null,
        error: error as Error | null,
      };
    }),
    fetchAll(async (from, to) => {
      const { data, error } = await supabase
        .from("targets")
        .select(
          "scope_type, scope_key, scope_id, metric, period_type, period_start, target_value, effective_from",
        )
        .range(from, to);
      return {
        data:
          data
            ?.filter(
              (
                row,
              ): row is typeof row & {
                scope_type: string;
                metric: string;
                period_type: string;
                period_start: string;
                target_value: number;
              } =>
                row.scope_type != null &&
                row.metric != null &&
                row.period_type != null &&
                row.period_start != null &&
                row.target_value != null,
            )
            .map((row) => ({
              scope_type: row.scope_type as TargetRow["scope_type"],
              scope_key: row.scope_key,
              scope_id: row.scope_id,
              metric: row.metric,
              period_type: row.period_type as TargetRow["period_type"],
              period_start: row.period_start,
              target_value: Number(row.target_value),
              effective_from: row.effective_from,
            })) ?? null,
        error: error as Error | null,
      };
    }),
    fetchTargetMetricConfigs(department.id).catch(() => []),
  ]);

  return {
    teams,
    reps,
    dailyActivities,
    repPeriodMetrics,
    teamPeriodMetrics,
    targets,
    targetMetricConfigs,
  };
}

export async function fetchGridViewModel(
  department: DepartmentDefinition,
  asOf = nowToronto(),
): Promise<GridViewModel> {
  const raw = await fetchGridRawData(department, asOf);
  return assembleGrid(department, raw, asOf);
}
